import {FormikErrors, FormikTouched} from 'formik';
import {
    useEffect,
    useRef,
    Dispatch,
    SetStateAction,
    useMemo,
    useCallback,
} from 'react';
import {useDebouncedCallback} from 'use-debounce';
import {Product} from 'components/customer/Product/entity/Product';
import {ObjectSchema} from 'yup';
import {genericMessageHandler, useQueryParams} from 'shared/helpers';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {setProductValuesForUpdate} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {getTabErrors} from 'components/customer/Product/helpers/getTabErrors';
import {Structure} from 'components/customer/Product/entity/Structure';
import {useNotificationContext} from 'contexts/NotificationContext';
import {getIsSavingProduct} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {useProductContext} from 'contexts/ProductContext';
import {useAppContext} from 'contexts/ApplicationContext';
import {PreviewFormValues} from 'components/customer/Preview3D/types';
import {normalizeProductWithDrawers} from 'hooks/Product/helpers/normalizeDrawerValues';
import {selectFaceHeightErrors} from 'components/customer/Product/store/drawerRunnerSlice';
import {setProductUpdated} from 'components/customer/Product/store/productSlice';

const APPLIED_PANEL_ID = 3;

interface FormHelerProps {
    productFieldValidator: (
        values: Product,
        errors: FormikErrors<Product>
    ) => void;
    setFormValues: Dispatch<SetStateAction<Product>>;
    formSchema: ObjectSchema<object, object, object, ''>;
    isRoomPlannerUpdate: boolean;
    setTabErrors: React.Dispatch<React.SetStateAction<object>>;
    formStructure: Structure[];
    values: Product;
    errors: FormikErrors<Product>;
    touched: FormikTouched<Product>;
    validateForm: (values?: Product) => Promise<FormikErrors<Product>>;
    tabErrors: {[key: number]: number};
    productData: Product;
}

export const useProductFormHelper = ({
    formSchema,
    isRoomPlannerUpdate,
    tabErrors,
    setTabErrors,
    formStructure,
    touched,
    values,
    errors,
    validateForm,
    productFieldValidator,
    setFormValues,
}: FormHelerProps) => {
    const {userProfile} = useAppContext();
    const {addMessages, notify} = useNotificationContext();
    const dispatch = useAppDispatch();
    const isSavingProduct = useAppSelector(getIsSavingProduct);
    const faceHeightErrors = useAppSelector(selectFaceHeightErrors);
    const {cabinetId} = useQueryParams() as {cabinetId: string};
    const {productDataStore} = useProductContext();

    const previousDoorHingeLength = useRef<number>(undefined);
    const previousHingeAmount = useRef(
        values['door_hinge_amount'] ?? undefined
    );

    const manuHingeAmounts = useMemo(
        () => userProfile?.doorHingeHoles,
        [userProfile?.doorHingeHoles]
    );

    const cabinet = useMemo(() => {
        return productDataStore?.current?.cabinet?.attributes;
    }, [productDataStore?.current?.cabinet?.attributes]);

    const productHasDoors = useMemo(() => {
        // some cabinets including applied panels should skip hinge
        // validation but have doors = 1 in db, so check door_hang
        // here too
        if (cabinet && cabinet.doors && cabinet.door_hang) {
            return (
                cabinet.doors > 0 &&
                cabinet.product_type != APPLIED_PANEL_ID &&
                cabinet.door_hang.trim() != ''
            );
        }
        return false;
    }, [cabinet?.doors, cabinet?.product_type, cabinet?.door_hang]);

    const doorHingeLengthCabDimension = useMemo(
        () => (cabinet?.hinge_direction == 0 ? 'height' : 'width'),
        [cabinet]
    );
    // Updates with the length of the dimension where the
    // door hinges will be placed. This will be the height
    // for vertical doors and the width for horizontal
    // doors.
    const currentDoorHingeLength = useMemo(() => {
        if (!productHasDoors) {
            return;
        }

        if (cabinet && values) {
            if (cabinet.hinge_direction == 0) {
                // Vertical hinges
                let height = values.cabinet_height ?? 0;

                if (cabinet.oven == 1) {
                    height -= values.oven_opening_height ?? 0;
                }
                if (cabinet.microwave == 1) {
                    height -= values.microwave_opening_height ?? 0;
                }
                if (cabinet.drawers != undefined && cabinet.drawers > 0) {
                    height -= values.cabinet_total_drawer_height ?? 0;
                }

                return height;
            } else if (cabinet.hinge_direction == 1) {
                // Horizontal hinges
                // No additional components currently effect
                // the width ???
                return values['cabinet_width'];
            }
        }
    }, [
        values['cabinet_height'],
        values['cabinet_width'],
        values['cabinet_total_drawer_height'],
        values['oven_opening_height'],
        values['microwave_opening_height'],
    ]);

    useEffect(() => {
        if (
            !productHasDoors ||
            previousDoorHingeLength == undefined ||
            currentDoorHingeLength == undefined ||
            previousDoorHingeLength.current == currentDoorHingeLength
        ) {
            return;
        }

        if (manuHingeAmounts) {
            if (currentDoorHingeLength < manuHingeAmounts[3]) {
                values['door_hinge_amount'] = 2;
            } else if (currentDoorHingeLength < manuHingeAmounts[4]) {
                values['door_hinge_amount'] = 3;
            } else if (currentDoorHingeLength < manuHingeAmounts[5]) {
                values['door_hinge_amount'] = 4;
            } else {
                values['door_hinge_amount'] = 5;
            }
        }

        if (previousHingeAmount.current != values['door_hinge_amount']) {
            genericMessageHandler(
                notify,
                [
                    'Door hinge amount has been adjusted to match the new door ' +
                        doorHingeLengthCabDimension +
                        '.',
                ],
                'warning'
            );
        }

        previousHingeAmount.current = values['door_hinge_amount'];
        previousDoorHingeLength.current = currentDoorHingeLength;
    }, [currentDoorHingeLength]);

    // a held face height is not in the values, so it brings its own run
    useEffect(() => {
        productFieldValidator(values, errors);
        setFormValues(values);
    }, [values, errors, faceHeightErrors]);

    useEffect(() => {
        void validateForm();
    }, [formSchema]);

    // Track previous shelf type to detect changes
    const prevShelfTypeRef = useRef(values.cabinet_simple_shelves);

    const setRoomPlannerValues = useDebouncedCallback(
        () => {
            // When switching cabinets fast, the URL cabinetId changes before
            // formik reinitialises with the new cabinet's data. The values
            // carry the id they were loaded for, so skip until they match.
            if (
                isRoomPlannerUpdate &&
                String(values.job_cabinet_id) === String(cabinetId) &&
                Object.keys(tabErrors).length === 0
            ) {
                const updateData = {
                    ...values,
                    job_cabinet_id: Number(cabinetId),
                };

                // Check if shelf type has changed
                const shelfTypeChanged =
                    prevShelfTypeRef.current !== values.cabinet_simple_shelves;

                if (shelfTypeChanged) {
                    // For shelf type changes, trigger immediate commit to 3D scene
                    // but keep edit sidebar open
                    prevShelfTypeRef.current = values.cabinet_simple_shelves;

                    // Ensure all shelf-related data is included
                    if (!values.cabinet_simple_shelves) {
                        if (values.shelves) {
                            updateData.shelves = values.shelves;
                        }
                        if (values.upper_shelves) {
                            updateData.upper_shelves = values.upper_shelves;
                        }
                        if (values.lower_shelves) {
                            updateData.lower_shelves = values.lower_shelves;
                        }
                        if (values.middle_shelves) {
                            updateData.middle_shelves = values.middle_shelves;
                        }
                    }

                    // Mark this as a shelf-specific update
                    updateData._isShelfTypeUpdate = true;
                }

                if (values.drawer_face_height) {
                    const normalized = normalizeProductWithDrawers(
                        values,
                        true
                    );

                    Object.assign(updateData, normalized);
                }

                const dispatchData = {
                    ...updateData,
                    isSavingProduct,
                    cabinet_cover_void:
                        updateData.cabinet_cover_void !== undefined
                            ? Boolean(updateData.cabinet_cover_void)
                            : undefined,
                };

                dispatch(
                    setProductValuesForUpdate(
                        dispatchData as unknown as PreviewFormValues
                    )
                );
            }
        },
        300 // Reduced debounce for faster shelf updates
    );

    const displayErrors = useCallback(() => {
        void validateForm(values).then((err) => {
            const errorFields = Object.keys(err);
            if (errorFields.length) {
                const messages = errorFields.map((field) => ({
                    message: err[field as keyof Product] as string,
                    fields: [field],
                    onlyField: true,
                }));

                if (addMessages) {
                    addMessages({
                        type: 'error',
                        messages,
                    });
                }
                setTabErrors(getTabErrors(formStructure, messages));
            } else {
                if (setTabErrors && addMessages) {
                    setTabErrors({});
                    addMessages({});
                }
            }
        });
    }, [validateForm, values]);

    useEffect(() => {
        void setRoomPlannerValues();
    }, [isRoomPlannerUpdate, values, tabErrors, isSavingProduct]);

    useEffect(() => {
        displayErrors();
    }, [errors]);

    useEffect(() => {
        if (touched && Object.keys(touched).length > 0) {
            dispatch(setProductUpdated(true));
        }

        return () => {
            dispatch(setProductUpdated(false));
        };
    }, [touched]);
};
